import { bankersRoundWithMax, debounce } from '@/utils/utils'
import { useCartStore } from '@/pinia/cart'
import H5ExceedQuantityDialog from '@/components/H5/ExceedQuantityDialog/index.vue'
import ExceedQuantityDialog from '@/components/ExceedQuantityDialog/index.vue'
import { usePlugin } from '@/hooks/usePlugin'
import { useCurrencyStore } from '@/pinia/currency'
import { createFuncall } from 'vue-funcall'
import { useCart } from './useCart'
import { deviceType } from '@/utils/constants'
import { removeCart, updateCart as updateCartCount } from '@/api/cart'

export const useCartSku = (sku) => {
  const cartStore = useCartStore()

  const currency = useCurrencyStore()

  const { cartList, isUnavailable } = useCart()

  const { $toast, $confirm } = usePlugin()

  const unit = computed(() => bankersRoundWithMax(currency.current.rate * sku.value.price, 0.01))
  const price = computed(() => (unit.value * sku.value.quantity).toFixed(2))

  const handleExceedQuantity = async () => {
    const Component = deviceType === 'MOBILE' ? H5ExceedQuantityDialog : ExceedQuantityDialog
    const { close } = createFuncall(Component, {
      quantity: sku.value.batchNum,
      onClose: () => {
        close()
      }
    })
    sku.value.quantity = calcMinAvailableQuantity()
  }

  watch(() => sku.value.quantity, () => {
    calcBundleQuantity()
  }, {
    flush: 'post'
  })

  /**
   * 监听各类异常情况并处理
   */
  watchEffect(() => {
    /**
     * 商品本身状态不可用处理
     */
    const unavailable = isUnavailable(sku.value.cartStatus)
    if (unavailable) {
      sku.value.disabled = true
      sku.value.checked = false
      sku.value.unavailableMsg = null
      return
    }
    /**
     * 商品类型为 服务 时，商品数量 <= MOQ 不可用处理
     */
    if (sku.value.skuType === 2) {
      const spu = cartList.value.find(spu => spu.offerId === sku.value.spuId)
      let spuProductsCount = 0
      spu.skuDetails.forEach(item => {
        if (item.skuType === 2) return
        if (item.checked) spuProductsCount += item.quantity
      })
      if (spuProductsCount < sku.value.canUseMoq) {
        sku.value.disabled = true
        sku.value.checked = false
        sku.value.unavailableMsg = `The product MOQ quantity does not meet the minimum requirement of ${sku.value.canUseMoq}.`
        return
      }
    }

    /**
     * 商品状态不可送达时，不可用处理
     */
    if (sku.value.undeliverable) {
      sku.value.unavailableMsg = 'Unable to deliver.'
      return
    }

    sku.value.disabled = false
    sku.value.unavailableMsg = null
  })

  watch(() => sku.value.quantity, debounce(() => {
    updateCart()
  }, 500))

  /**
   * 计算当前 SKU 的最小可购买数量
   */
  const calcMinAvailableQuantity = () => {
    let minQuantity = sku.value.batchNum * 2
    const spu = cartList.value.find(spu => spu.offerId === sku.value.spuId)
    spu.skuDetails.forEach(item => {
      if (item.skuId !== sku.value.skuId) {
        minQuantity -= item.quantity
      }
    })
    return minQuantity
  }

  const handleCheck = () => {
    if (sku.value.disabled) return

    sku.value.checked = !sku.value.checked
  }

  // 捆绑数量算法
  const calcBundleQuantity = () => {
    const data = sku.value
    const bundleQuantity = +data.bundleQuantity
    if (bundleQuantity === 1) return

    const halfBundle = bundleQuantity / 2
    let quantity = data.quantity
    quantity = (Math.floor((quantity + halfBundle) / bundleQuantity)) * bundleQuantity
    if (quantity <= 0) {
      quantity = bundleQuantity
    }
    data.quantity = quantity
  }

  const updateCart = async () => {
    const { code } = await updateCartCount({
      cartId: sku.value.cartId,
      quantity: sku.value.quantity
    })
    if (code === 200) {
      cartStore.updateCartCount()
    } else if (code === 100001) {
      handleExceedQuantity()
    } else if (code === 200001) {
      $toast('Please fill delivery address.')
      localStorage.removeItem('addressForm')
    }
  }

  const handleDeleteClick = () => {
    $confirm('Delete item?', 'Are you sure you want to delete item from cart?').then(async () => {
      handleDelete()
    })
  }

  const handleDelete = async () => {
    const { code } = await removeCart({
      cartIds: [sku.value.cartId]
    })
    if (code === 200) {
      $toast('Deleted successfully.', { type: 'success' })
      cartStore.updateCartCount()
      const spu = cartList.value.find(spu => spu.offerId === sku.value.spuId)
      if (spu.skuDetails.length === 1) {
        cartList.value.splice(cartList.value.indexOf(spu), 1)
      } else {
        spu.skuDetails.splice(spu.skuDetails.indexOf(sku.value), 1)
      }
    }
  }

  return {
    unit,
    price,
    handleCheck,
    handleDelete,
    handleDeleteClick
  }
}
